import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Filter,
  RefreshCw,
  Search,
  Zap,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Stat,
} from '../ui/index.js';
import {
  fetchCrawlsMonitoring,
  triggerCronJob,
  cancelCrawlJob,
  type CrawlsMonitoringResponse,
} from '../api.js';

function formatCountdown(ms: number | null): string {
  if (ms === null || ms <= 0) return 'Imminent';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (min < 60) return remSec > 0 ? `${min}m ${remSec}s` : `${min}m`;
  const hours = Math.floor(min / 60);
  const remMin = min % 60;
  if (hours < 24) return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `${days}j ${remHours}h`;
}

function formatDateTunis(isoString: string | null): string {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Africa/Tunis',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(d);
  } catch {
    return isoString;
  }
}

export function SettingsCrawlsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(5000); // 5s default for real-time monitoring
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<'all' | 'Successed' | 'Failed'>('all');
  const [historySearch, setHistorySearch] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [selectedError, setSelectedError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<CrawlsMonitoringResponse>({
    queryKey: ['crawls-monitoring'],
    queryFn: fetchCrawlsMonitoring,
    refetchInterval: autoRefreshInterval > 0 ? autoRefreshInterval : false,
  });

  const triggerMutation = useMutation({
    mutationFn: ({ jobName, queueName }: { jobName: string; queueName?: string }) =>
      triggerCronJob(jobName, queueName),
    onSuccess: (res, vars) => {
      setToastMessage({
        text: `Tâche "${vars.jobName}" déclenchée avec succès (Job ID: ${res.jobId ?? 'en file d\'attente'})`,
        type: 'success',
      });
      void queryClient.invalidateQueries({ queryKey: ['crawls-monitoring'] });
    },
    onError: (err) => {
      setToastMessage({
        text: `Erreur au déclenchement : ${err instanceof Error ? err.message : String(err)}`,
        type: 'error',
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ jobId, queueName }: { jobId: string; queueName: string }) =>
      cancelCrawlJob(jobId, queueName),
    onSuccess: () => {
      setToastMessage({ text: 'Crawl interrompu avec succès', type: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['crawls-monitoring'] });
    },
    onError: (err) => {
      setToastMessage({
        text: `Erreur d'interruption : ${err instanceof Error ? err.message : String(err)}`,
        type: 'error',
      });
    },
  });

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const runningCrawls = data?.runningCrawls ?? [];
  const cronSchedulers = data?.cronSchedulers ?? [];
  const lastExecutions = data?.lastExecutions ?? [];

  // Categories list for filter
  const categories = ['all', ...Array.from(new Set(cronSchedulers.map((c) => c.category)))];

  const filteredCrons = cronSchedulers.filter((c) => {
    if (selectedCategory === 'all') return true;
    return c.category === selectedCategory;
  });

  const filteredHistory = lastExecutions.filter((item) => {
    if (historyStatusFilter !== 'all' && item.status !== historyStatusFilter) return false;
    if (historySearch.trim() !== '') {
      const query = historySearch.toLowerCase().trim();
      const matchName = item.name.toLowerCase().includes(query);
      const matchLabel = item.label.toLowerCase().includes(query);
      const matchCategory = item.category.toLowerCase().includes(query);
      const matchError = item.error ? item.error.toLowerCase().includes(query) : false;
      return matchName || matchLabel || matchCategory || matchError;
    }
    return true;
  });

  // Calculate success rate over the last 50 executions
  const successCount = lastExecutions.filter((e) => e.status === 'Successed').length;
  const failedCount = lastExecutions.filter((e) => e.status === 'Failed').length;
  const totalCompleted = successCount + failedCount;
  const successRate = totalCompleted > 0 ? Math.round((successCount / totalCompleted) * 100) : 100;

  return (
    <div>
      {/* Page Header */}
      <PageHeader
        index="S12"
        title="Surveillance des Crawls & Tâches Crons"
        purpose="Monitoring en temps réel de tous les crawlers, tâches d'arrière-plan BullMQ et historique d'exécution"
        meta={[
          { label: 'Crawls actifs', value: String(runningCrawls.length) },
          { label: 'Tâches planifiées', value: String(cronSchedulers.length) },
          { label: 'Taux de succès', value: `${successRate} %` },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Auto refresh pill */}
            <div className="flex items-center rounded-lg border border-border bg-card p-1 text-xs">
              <span className="px-2 font-medium text-ink-muted">Rafraîchissement :</span>
              {[
                { label: '5s', value: 5000 },
                { label: '10s', value: 10000 },
                { label: '30s', value: 30000 },
                { label: 'Pause', value: 0 },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAutoRefreshInterval(opt.value)}
                  className={`rounded px-2 py-0.5 font-semibold transition-colors ${
                    autoRefreshInterval === opt.value
                      ? 'bg-herb-600 text-white shadow-xs'
                      : 'text-ink-soft hover:bg-cream hover:text-ink'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="flex items-center gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin text-herb-600' : ''}`} />
              <span>Actualiser</span>
            </Button>
          </div>
        }
      />

      <div className="space-y-4">
        {/* Toast Feedback */}
        {toastMessage && (
          <div className="fixed right-5 top-20 z-50 max-w-md animate-rise" role="status">
            <Alert
              tone={toastMessage.type === 'success' ? 'good' : 'alarm'}
              title={toastMessage.type === 'success' ? 'Opération réussie' : 'Erreur'}
              actions={
                <button
                  type="button"
                  onClick={() => setToastMessage(null)}
                  className="cursor-pointer rounded p-1 text-xs font-semibold opacity-70 hover:opacity-100"
                  aria-label="Fermer"
                >
                  ✕
                </button>
              }
            >
              {toastMessage.text}
            </Alert>
          </div>
        )}

        {/* KPI Stats Overview */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Crawls Actifs"
            value={String(runningCrawls.length)}
            tone={runningCrawls.length > 0 ? 'good' : 'neutral'}
            hint={runningCrawls.length > 0 ? 'Exécution en direct' : 'Aucun crawl en cours'}
          />
          <Stat
            label="Tâches Planifiées (Crons)"
            value={String(cronSchedulers.length)}
            tone="neutral"
            hint={`${cronSchedulers.filter((c) => c.isRunning).length} active(s) en ce moment`}
          />
          <Stat
            label="Taux de Succès (50 der.)"
            value={`${successRate} %`}
            tone={successRate >= 90 ? 'good' : successRate >= 75 ? 'signal' : 'alarm'}
            hint={`${successCount} réussies · ${failedCount} échecs`}
          />
          <Stat
            label="Dernière Exécution"
            value={lastExecutions[0] ? lastExecutions[0].status : '—'}
            tone={lastExecutions[0]?.status === 'Successed' ? 'good' : lastExecutions[0]?.status === 'Failed' ? 'alarm' : 'neutral'}
            hint={lastExecutions[0] ? `${lastExecutions[0].label} (${lastExecutions[0].durationFormatted})` : 'Aucune donnée'}
          />
        </div>

      {isLoading && (
        <div className="p-12 flex justify-center">
          <Loading label="Chargement des données de monitoring…" />
        </div>
      )}

      {isError && (
        <Alert tone="alarm" title="Erreur de connexion">
          {error instanceof Error ? error.message : 'Impossible de récupérer l\'état des crawlers et crons'}
        </Alert>
      )}

      {!isLoading && !isError && (
        <>
          {/* ========================================================================= */}
          {/* SECTION 1: CRAWLS EN COURS (RUNNING CRAWLS WITH PROGRESS BAR)             */}
          {/* ========================================================================= */}
          <Panel>
            <PanelHeader
              title="Crawls en cours d'exécution"
              hint="Mise à jour en temps réel"
              actions={
                runningCrawls.length > 0 ? (
                  <Badge tone="good">
                    {runningCrawls.length} en cours
                  </Badge>
                ) : undefined
              }
            />

            <PanelBody>
              {runningCrawls.length === 0 ? (
                <div className="py-6 text-center">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-cream text-ink-faint mb-3">
                    <Activity className="h-6 w-6 text-ink-ghost" />
                  </div>
                  <h4 className="text-sm font-semibold text-ink">Aucun crawl en cours d'exécution</h4>
                  <p className="mt-1 text-xs text-ink-muted max-w-md mx-auto">
                    Tous les crawlers concurrents et scripts d'enrichissement sont actuellement inactifs.
                    Le prochain crawl démarrera automatiquement selon le calendrier planifié ci-dessous.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {runningCrawls.map((crawl) => (
                    <div
                      key={crawl.id}
                      className={`rounded-xl border p-5 shadow-xs ${
                        crawl.isWaiting
                          ? 'border-amber-300/80 bg-gradient-to-br from-amber-50/50 to-white'
                          : 'border-emerald-300/80 bg-gradient-to-br from-emerald-50/50 to-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-base text-ink">{crawl.label}</h3>
                            <span className="relative flex h-2.5 w-2.5">
                              <span
                                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                                  crawl.isWaiting ? 'bg-amber-400' : 'bg-emerald-400'
                                }`}
                              />
                              <span
                                className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                                  crawl.isWaiting ? 'bg-amber-600' : 'bg-emerald-600'
                                }`}
                              />
                            </span>
                          </div>
                          <p className="text-xs text-ink-muted mt-0.5">
                            File : <code className={`font-mono ${crawl.isWaiting ? 'text-amber-800' : 'text-emerald-800'}`}>{crawl.queue}</code> · Source :{' '}
                            <code className={`font-mono ${crawl.isWaiting ? 'text-amber-800' : 'text-emerald-800'}`}>{crawl.name}</code>
                          </p>
                        </div>
                        <Badge tone={crawl.isWaiting ? 'signal' : 'good'}>
                          {crawl.isWaiting ? `En attente (pos. ${crawl.queuePosition ?? 1})` : 'En cours'}
                        </Badge>
                      </div>

                      {/* Live Progress Bar */}
                      <div className="mt-4 space-y-1.5">
                        <div className="flex items-center justify-between text-xs font-semibold">
                          <span className="text-ink">{crawl.progress.stage}</span>
                          <span className={`font-mono tabular-nums ${crawl.isWaiting ? 'text-amber-700' : 'text-emerald-700'}`}>
                            {crawl.isWaiting
                              ? 'Créneau en attente…'
                              : crawl.progress.percent !== null
                                ? `${crawl.progress.percent} %`
                                : 'Progression…'}
                          </span>
                        </div>
                        <div
                          className={`h-3 w-full overflow-hidden rounded-full bg-cream border ${
                            crawl.isWaiting ? 'border-amber-200' : 'border-emerald-200'
                          }`}
                        >
                          {crawl.isWaiting ? (
                            <div className="h-full w-full animate-pulse rounded-full bg-amber-400/70" />
                          ) : crawl.progress.percent !== null ? (
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 transition-all duration-300"
                              style={{ width: `${Math.max(5, Math.min(100, crawl.progress.percent))}%` }}
                            />
                          ) : (
                            <div className="h-full w-1/3 animate-pulse rounded-full bg-emerald-500" />
                          )}
                        </div>
                      </div>

                      {/* Metrics and duration footer */}
                      <div className="mt-4 pt-3 border-t border-emerald-100 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-muted">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1 font-medium">
                            <Clock className="h-3.5 w-3.5 text-ink-ghost" />
                            <span>Depuis {formatCountdown(crawl.durationMs)}</span>
                          </span>
                          {crawl.metrics.urlsFetched !== undefined && (
                            <span>{crawl.metrics.urlsFetched} URLs visitées</span>
                          )}
                          {crawl.metrics.offersCreated !== undefined && (
                            <span className="text-emerald-700 font-semibold">
                              +{crawl.metrics.offersCreated} créées
                            </span>
                          )}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs text-rose-700 hover:bg-rose-50 border-rose-200"
                          onClick={() => {
                            if (confirm(`Êtes-vous sûr de vouloir interrompre le crawl "${crawl.label}" ?`)) {
                              cancelMutation.mutate({ jobId: crawl.jobId, queueName: crawl.queue });
                            }
                          }}
                          disabled={cancelMutation.isPending}
                        >
                          Interrompre
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </PanelBody>
          </Panel>

          {/* ========================================================================= */}
          {/* SECTION 2: TÂCHES PLANIFIÉES (CRONS) AVEC BARRE DE PROGRESSION            */}
          {/* ========================================================================= */}
          <Panel>
            <PanelHeader
              title="Toutes les Tâches Planifiées (Crons)"
              hint="Visualisation du cycle de déclenchement, progression avant la prochaine exécution et déclenchement manuel"
              actions={
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-ink-faint mr-1 flex items-center gap-1">
                    <Filter className="h-3 w-3" /> Catégorie :
                  </span>
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        selectedCategory === cat
                          ? 'bg-herb-700 text-white shadow-xs'
                          : 'bg-cream text-ink-muted hover:bg-cream-dark hover:text-ink'
                      }`}
                    >
                      {cat === 'all' ? `Toutes (${cronSchedulers.length})` : cat}
                    </button>
                  ))}
                </div>
              }
            />

            <PanelBody>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredCrons.map((cron) => (
                  <div
                    key={cron.id}
                    className={`rounded-xl border p-4 transition-shadow hover:shadow-xs flex flex-col justify-between ${
                      cron.isRunning
                        ? 'border-emerald-400 bg-emerald-50/30'
                        : 'border-border bg-card'
                    }`}
                  >
                    <div>
                      {/* Top Header of Cron Card */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-sm text-ink truncate">{cron.label}</h4>
                            {cron.isRunning && (
                              <Badge tone="good">
                                Actif
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-ink-muted mt-0.5 line-clamp-1">{cron.description}</p>
                        </div>
                        <span className="text-[10px] font-mono font-medium px-2 py-0.5 rounded-full bg-cream text-ink-soft border border-border shrink-0">
                          {cron.queue}
                        </span>
                      </div>

                      {/* Schedule specification */}
                      <div className="mt-3 flex items-center justify-between text-xs text-ink-soft bg-cream/50 rounded-lg px-2.5 py-1.5 border border-border/60">
                        <span className="font-medium text-ink">{cron.humanSchedule}</span>
                        <code className="font-mono text-[11px] text-ink-faint bg-white px-1.5 py-0.5 rounded border border-border">
                          {cron.pattern}
                        </code>
                      </div>

                      {/* PROGRESS BAR SECTION */}
                      <div className="mt-3.5 space-y-1.5">
                        {cron.isRunning ? (
                          // Cron is currently running: render execution progress bar
                          <div>
                            <div className="flex items-center justify-between text-xs font-semibold text-emerald-800 mb-1">
                              <span>Exécution en direct : {cron.activeProgress?.stage ?? 'En cours'}</span>
                              <span className="font-mono">{cron.activeProgress?.percent ?? 50} %</span>
                            </div>
                            <div className="h-2.5 w-full overflow-hidden rounded-full bg-emerald-100 border border-emerald-300">
                              <div
                                className="h-full rounded-full bg-emerald-600 animate-pulse transition-all duration-300"
                                style={{ width: `${Math.max(10, Math.min(100, cron.activeProgress?.percent ?? 50))}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          // Cron is waiting: render cycle countdown progress bar
                          <div>
                            <div className="flex items-center justify-between text-[11px] text-ink-muted mb-1">
                              <span className="flex items-center gap-1 font-medium">
                                <Clock className="h-3 w-3 text-ink-ghost" />
                                <span>Prochaine dans : <strong>{formatCountdown(cron.nextRunInMs)}</strong></span>
                              </span>
                              <span className="font-mono text-[10px] text-ink-ghost">
                                {cron.timeUntilNextPercent} % cycle
                              </span>
                            </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-cream border border-border/80">
                              <div
                                className="h-full rounded-full bg-herb-500 transition-all duration-500"
                                style={{ width: `${cron.timeUntilNextPercent}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer with last run status & trigger button */}
                    <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between gap-2">
                      <div className="text-xs">
                        {cron.isRunning ? (
                          <div className="flex items-center gap-1.5">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                            </span>
                            <span className="text-emerald-700 font-semibold text-[11px]">En cours d&apos;exécution…</span>
                          </div>
                        ) : cron.isQueued ? (
                          <div className="flex items-center gap-1.5">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                            </span>
                            <span className="text-amber-700 font-semibold text-[11px]">
                              En file d&apos;attente (pos. {cron.queuePosition ?? 1})
                            </span>
                          </div>
                        ) : cron.lastRun ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-ink-ghost text-[11px]">Dernier :</span>
                            <Badge tone={cron.lastRun.status === 'Successed' ? 'good' : 'alarm'}>
                              {cron.lastRun.status}
                            </Badge>
                            {cron.lastRun.durationMs !== null && (
                              <span className="text-[11px] font-mono text-ink-faint">
                                ({Math.round(cron.lastRun.durationMs / 1000)}s)
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-ink-ghost text-[11px]">Jamais exécuté</span>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        disabled={cron.isRunning || cron.isQueued || triggerMutation.isPending}
                        onClick={() => {
                          triggerMutation.mutate({ jobName: cron.jobName, queueName: cron.queue });
                        }}
                        className="text-xs flex items-center gap-1 px-2.5 py-1 hover:border-herb-500 hover:text-herb-700"
                      >
                        {cron.isRunning ? (
                          <>
                            <RefreshCw className="h-3 w-3 animate-spin text-emerald-600" />
                            <span>En cours…</span>
                          </>
                        ) : cron.isQueued ? (
                          <>
                            <Clock className="h-3 w-3 animate-pulse text-amber-600" />
                            <span>En attente…</span>
                          </>
                        ) : (
                          <>
                            <Zap className="h-3 w-3 text-amber-500" />
                            <span>Lancer</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </PanelBody>
          </Panel>

          {/* ========================================================================= */}
          {/* SECTION 3: 50 DERNIÈRES EXÉCUTIONS (CRAWL ET CRON AVEC BADGE)             */}
          {/* ========================================================================= */}
          <Panel>
            <PanelHeader
              title="50 Dernières Exécutions (Crawls & Crons)"
              hint="Historique consolidé des jobs BullMQ, CrawlRuns et synchronisations avec statuts exacts Successed / Failed"
              actions={
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-cream border border-border text-ink">
                    {lastExecutions.length} enregistrements
                  </span>
                  <div className="flex items-center rounded-lg border border-border bg-card p-0.5 text-xs">
                    {(['all', 'Successed', 'Failed'] as const).map((st) => (
                      <button
                        key={st}
                        type="button"
                        onClick={() => setHistoryStatusFilter(st)}
                        className={`px-2.5 py-1 rounded font-medium transition-colors ${
                          historyStatusFilter === st
                            ? st === 'Successed'
                              ? 'bg-emerald-600 text-white'
                              : st === 'Failed'
                                ? 'bg-rose-600 text-white'
                                : 'bg-herb-700 text-white'
                            : 'text-ink-muted hover:text-ink'
                        }`}
                      >
                        {st === 'all' ? 'Tous' : st}
                      </button>
                    ))}
                  </div>

                  <div className="relative">
                    <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-ghost" />
                    <input
                      type="text"
                      placeholder="Filtrer tâche ou erreur…"
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      className="rounded-lg border border-border bg-card pl-8 pr-3 py-1 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-500 focus:outline-hidden w-48"
                    />
                  </div>
                </div>
              }
            />

            <PanelBody className="p-0">
              {filteredHistory.length === 0 ? (
                <div className="p-8 text-center text-xs text-ink-muted">
                  Aucune exécution ne correspond aux filtres appliqués.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-border bg-cream/40 text-ink-soft">
                        <th className="py-2.5 px-4 font-semibold">Statut</th>
                        <th className="py-2.5 px-4 font-semibold">Tâche / Crawl</th>
                        <th className="py-2.5 px-4 font-semibold">Catégorie</th>
                        <th className="py-2.5 px-4 font-semibold">Démarré le</th>
                        <th className="py-2.5 px-4 font-semibold">Durée</th>
                        <th className="py-2.5 px-4 font-semibold">Résultats & Métriques</th>
                        <th className="py-2.5 px-4 font-semibold text-right">Détails</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {filteredHistory.map((item) => (
                        <tr key={item.id} className="hover:bg-cream/20 transition-colors">
                          {/* Statut Badge: EXACTLY Successed or Failed */}
                          <td className="py-3 px-4 whitespace-nowrap">
                            <Badge
                              tone={
                                item.status === 'Successed'
                                  ? 'good'
                                  : item.status === 'Failed'
                                    ? 'alarm'
                                    : 'signal'
                              }
                            >
                              {item.status}
                            </Badge>
                          </td>

                          {/* Task Name & Label */}
                          <td className="py-3 px-4">
                            <div className="font-semibold text-ink">{item.label}</div>
                            <div className="font-mono text-[11px] text-ink-ghost mt-0.5">
                              {item.name} · <span className="text-ink-faint">{item.queue}</span>
                            </div>
                          </td>

                          {/* Category */}
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-cream border border-border text-ink-muted">
                              {item.category}
                            </span>
                          </td>

                          {/* Started At in Tunis time */}
                          <td className="py-3 px-4 whitespace-nowrap text-ink-soft font-mono text-[11px]">
                            {formatDateTunis(item.startedAt)}
                          </td>

                          {/* Duration */}
                          <td className="py-3 px-4 whitespace-nowrap font-mono text-ink">
                            {item.durationFormatted}
                          </td>

                          {/* Volume & Metrics */}
                          <td className="py-3 px-4 text-ink-soft">
                            {item.metrics.details ? (
                              <span>{item.metrics.details}</span>
                            ) : item.metrics.urlsFetched !== undefined ? (
                              <span>
                                {item.metrics.urlsFetched} fiches ({item.metrics.offersCreated ?? 0} créées,{' '}
                                {item.metrics.offersUpdated ?? 0} màj)
                              </span>
                            ) : (
                              <span className="text-ink-ghost">—</span>
                            )}
                          </td>

                          {/* Error / Inspect Details */}
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            {item.error ? (
                              <button
                                type="button"
                                onClick={() => setSelectedError(item.error)}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-700 hover:text-rose-900 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-2 py-0.5 rounded"
                              >
                                <AlertTriangle className="h-3 w-3" />
                                <span>Voir l'erreur</span>
                              </button>
                            ) : (
                              <span className="text-emerald-700 text-[11px] font-medium flex items-center justify-end gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                <span>OK</span>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </PanelBody>
          </Panel>
        </>
      )}
      </div>

      {/* Error Inspection Modal */}
      {selectedError &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs overflow-y-auto animate-fade"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedError(null);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="max-w-2xl w-full rounded-2xl bg-card border border-border p-6 shadow-2xl my-auto animate-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between pb-3 border-b border-border">
                <div className="flex items-center gap-2 text-rose-700 font-bold text-base">
                  <AlertTriangle className="h-5 w-5" />
                  <span>Rapport d'Erreur d'Exécution</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedError(null)}
                  className="text-ink-ghost hover:text-ink p-1 rounded cursor-pointer"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4">
                <p className="text-xs text-ink-muted mb-2">Message d'erreur retourné par le worker :</p>
                <pre className="p-3 bg-rose-50 border border-rose-200 text-rose-900 rounded-lg font-mono text-xs overflow-x-auto whitespace-pre-wrap max-h-80">
                  {selectedError}
                </pre>
              </div>
              <div className="mt-6 flex justify-end">
                <Button variant="outline" onClick={() => setSelectedError(null)}>
                  Fermer
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
